import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { config } from '../../../../config';
import { useLimits } from '../../plan/hooks/limits';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { SettingsSection } from '../../../components/ui/settings-section';

const MCPIntegration: React.FC = () => {
  const { t } = useTranslation();
  const guardLimit = useLimits();

  // Dialog states
  const [claudeDialogOpen, setClaudeDialogOpen] = useState(false);
  const [chatGPTDialogOpen, setChatGPTDialogOpen] = useState(false);

  // Get base URL from authBaseUrlV2
  const baseUrlWithVersion = config.authBaseUrlV2;
  const mcpUrl = `${baseUrlWithVersion}/mcp/sse`;

  const clientId = config.mcpClaudeClientId;
  const clientSecret = config.mcpClaudeClientSecret;

  const handleConnectClaude = () => {
    guardLimit('integrations', () => {
      setClaudeDialogOpen(true);
    });
  };

  const handleConnectChatGPT = () => {
    guardLimit('integrations', () => {
      setChatGPTDialogOpen(true);
    });
  };

  const ServerUrlDisplay = () => {
    return (
      <span className="select-all break-all text-muted-foreground">
        {mcpUrl}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Claude Integration Row */}
      <SettingsSection
        label={t(
          'settings.integrations.mcp.claude.title',
          'Connect to Claude (MCP)',
        )}
        description={t(
          'settings.integrations.mcp.claude.description',
          'Access your recordings via MCP connector',
        )}
      >
        <Button variant="ghost" onClick={handleConnectClaude}>
          {t('common.connect', 'Connect')}
        </Button>
      </SettingsSection>

      {/* ChatGPT Integration Row */}
      <SettingsSection
        label={t(
          'settings.integrations.mcp.chatgpt.title',
          'Connect to ChatGPT (MCP)',
        )}
        description={t(
          'settings.integrations.mcp.chatgpt.description',
          'Access your recordings via MCP connector',
        )}
        showSeparator={false}
      >
        <Button variant="ghost" onClick={handleConnectChatGPT}>
          {t('common.connect', 'Connect')}
        </Button>
      </SettingsSection>

      {/* Claude Dialog */}
      <Dialog open={claudeDialogOpen} onOpenChange={setClaudeDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t(
                'settings.integrations.mcp.claude.dialogTitle',
                'Connect to Claude (MCP)',
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* Minimal Pro Notice */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded-md border border-transparent">
              <Info className="h-4 w-4 shrink-0" />
              <span>
                Requires{' '}
                <span className="font-medium text-foreground">Claude Pro</span>{' '}
                subscription.
              </span>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">
                {t(
                  'settings.integrations.mcp.claude.step1',
                  'Step 1: Open Claude Connectors',
                )}
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-1">
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step1_1',
                    'Open Claude (web at claude.ai or desktop app)',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step1_2',
                    'Go to Settings',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step1_3',
                    'Click Connectors',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step1_4',
                    'Click Add custom connector',
                  )}
                </li>
              </ol>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">
                {t(
                  'settings.integrations.mcp.claude.step2',
                  'Step 2: Add ScreenApp Connector',
                )}
              </h4>
              {/* Connection Details */}
              <div className="rounded-md border p-4 space-y-3">
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Name
                  </span>
                  <span className="text-sm">ScreenApp</span>
                </div>
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Server URL
                  </span>
                  <div className="text-sm">
                    <ServerUrlDisplay />
                  </div>
                </div>
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Authentication
                  </span>
                  <span className="text-sm">OAuth 2.0</span>
                </div>
              </div>

              {clientSecret && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-medium">
                    {t(
                      'settings.integrations.mcp.claude.oauth',
                      'OAuth Configuration:',
                    )}
                  </p>
                  <div className="rounded-md border p-4 space-y-3">
                    <div className="grid gap-1">
                      <span className="text-xs font-medium text-muted-foreground">
                        Client ID
                      </span>
                      <span className="text-sm select-all break-all text-muted-foreground">
                        {clientId}
                      </span>
                    </div>
                    <div className="grid gap-1">
                      <span className="text-xs font-medium text-muted-foreground">
                        Client Secret
                      </span>
                      <span className="text-sm select-all break-all text-muted-foreground">
                        {clientSecret}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">
                {t(
                  'settings.integrations.mcp.claude.step3',
                  'Step 3: Authorize',
                )}
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-1">
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step3_1',
                    'Click Save or Connect',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step3_2',
                    'Claude opens your browser to ScreenApp login',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step3_3',
                    'Log in with your ScreenApp account',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step3_4',
                    'Click Allow to grant Claude access',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.claude.step3_6',
                    'Connected! Look for the connector icon in Claude',
                  )}
                </li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setClaudeDialogOpen(false)}>
              {t('common.done', 'Done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ChatGPT Dialog */}
      <Dialog open={chatGPTDialogOpen} onOpenChange={setChatGPTDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t(
                'settings.integrations.mcp.chatgpt.dialogTitle',
                'Connect to ChatGPT (MCP)',
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            {/* Minimal Pro Notice */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded-md border border-transparent">
              <Info className="h-4 w-4 shrink-0" />
              <span>
                Requires{' '}
                <span className="font-medium text-foreground">
                  ChatGPT Plus
                </span>{' '}
                or <span className="font-medium text-foreground">Pro</span>{' '}
                subscription.
              </span>
            </div>

            {/* Steps */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">
                {t(
                  'settings.integrations.mcp.chatgpt.step1',
                  'Step 1: Open ChatGPT Connectors',
                )}
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-1">
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step1_1',
                    'Open ChatGPT (web or desktop app)',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step1_2',
                    'Go to Settings',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step1_3',
                    'Click Apps & connectors',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step1_4',
                    'Click Create',
                  )}
                </li>
              </ol>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">
                {t(
                  'settings.integrations.mcp.chatgpt.step2',
                  'Step 2: Enter Connection Details',
                )}
              </h4>
              <div className="rounded-md border p-4 space-y-3">
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Name
                  </span>
                  <span className="text-sm">ScreenApp</span>
                </div>
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Server URL
                  </span>
                  <div className="text-sm">
                    <ServerUrlDisplay />
                  </div>
                </div>
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Authentication
                  </span>
                  <span className="text-sm">OAuth 2.0</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.integrations.mcp.chatgpt.step2_note',
                  'ChatGPT will auto-register and redirect you to authorize.',
                )}
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">
                {t(
                  'settings.integrations.mcp.chatgpt.step3',
                  'Step 3: Authorize',
                )}
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-1">
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step3_1',
                    'ChatGPT opens your browser to ScreenApp login',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step3_2',
                    'Log in with your ScreenApp account',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step3_3',
                    'Click Allow to grant ChatGPT access',
                  )}
                </li>
                <li>
                  {t(
                    'settings.integrations.mcp.chatgpt.step3_5',
                    'Connected! You can now ask ChatGPT about your recordings',
                  )}
                </li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setChatGPTDialogOpen(false)}>
              {t('common.done', 'Done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MCPIntegration;
